import { Alert, Button, SuperEllipse, Typography, useTheme } from '@themenu/design-system'
import React, { type FC, useState } from 'react'
import { useIntl } from 'react-intl'

import { logEvent } from '../../services/datadog'
import { registerPasskey } from '../../services/webauthn'

interface PasskeyRegistrationPromptProps {
  onComplete: () => void
  onSkip: () => void
}

export const PasskeyRegistrationPrompt: FC<PasskeyRegistrationPromptProps> = ({ onComplete, onSkip }) => {
  const { formatMessage } = useIntl()
  const theme = useTheme()
  const [isRegistering, setIsRegistering] = useState(false)
  const [result, setResult] = useState<'idle' | 'success' | 'error'>('idle')

  const handleRegister = async () => {
    setIsRegistering(true)
    try {
      await registerPasskey()
      logEvent('passkey_registration_prompt_success')
      setResult('success')
    } catch (err) {
      logEvent('passkey_registration_prompt_failed', undefined, { error: String(err) })
      setResult('error')
    } finally {
      setIsRegistering(false)
    }
  }

  if (result === 'success') {
    return (
      <div className="flex w-full xs:max-w-md flex-1 flex-col items-center justify-center gap-24 px-20">
        <SuperEllipse size={56} colorSet="success" icon="check" />
        <Typography variant="h3" style={{ color: theme.colors.content.default }} className="text-center">
          {formatMessage({ id: 'passkey.registration_prompt.success' })}
        </Typography>
        <Typography variant="body" style={{ color: theme.colors.content.subdued }} className="text-center">
          {formatMessage({ id: 'passkey.registration_prompt.success_description' })}
        </Typography>
        <Button variant="primary" className="w-full" onClick={onComplete}>
          {formatMessage({ id: 'passkey.registration_prompt.continue' })}
        </Button>
      </div>
    )
  }

  return (
    <div className="flex w-full xs:max-w-md flex-1 flex-col items-center justify-center gap-24 px-20">
      <SuperEllipse size={56} colorSet="info" icon="key" />
      <Typography variant="h3" style={{ color: theme.colors.content.default }} className="text-center">
        {formatMessage({ id: 'passkey.registration_prompt.title' })}
      </Typography>
      {result === 'error' && (
        <Alert severity="error">{formatMessage({ id: 'passkey.registration_prompt.error' })}</Alert>
      )}
      <Typography variant="body" style={{ color: theme.colors.content.subdued }} className="text-center">
        {formatMessage({ id: 'passkey.registration_prompt.description' })}
      </Typography>
      <div className="flex w-full flex-col gap-12">
        <Button
          variant="primary"
          className="w-full"
          onClick={handleRegister}
          loading={isRegistering}
          disabled={isRegistering}
        >
          {formatMessage({ id: 'passkey.registration_prompt.action' })}
        </Button>
        <Button variant="alternativeText" className="w-full" onClick={onSkip} disabled={isRegistering}>
          {formatMessage({ id: 'passkey.registration_prompt.skip' })}
        </Button>
      </div>
    </div>
  )
}
